import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';

const ContactModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    company_name: '',
    name: '',
    email: '',
    phone: '',
    article_url: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // 送信先は同じサイト内の Cloudflare Pages Functions（frontend/functions/api/contacts.js）
      await axios.post('/api/contacts', formData);
      
      setIsSuccess(true);
      toast.success('お問い合わせを受け付けました');
      
      setTimeout(() => {
        setFormData({
          company_name: '',
          name: '',
          email: '',
          phone: '',
          article_url: '',
          message: '',
        });
        setIsSuccess(false);
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('送信に失敗しました。もう一度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white text-xl">
            無料診断のお申し込み
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            以下のフォームにご記入ください。担当者より1、2営業日以内にご連絡いたします。
          </DialogDescription>
        </DialogHeader>

        {isSuccess ? (
          <div className="py-12 text-center">
            <div className="w-16 h-16 bg-cyan-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-cyan-400" />
            </div>
            <h3 className="text-white font-semibold text-lg mb-2">
              お問い合わせありがとうございます
            </h3>
            <p className="text-slate-400 text-sm">
              担当者よりご連絡いたしますので、少々お待ちください。
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company_name" className="text-slate-300">
                  会社名 <span className="text-red-400">*</span>
                </Label>
                <Input
                  id="company_name"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  required
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
                  placeholder="株式会社○○"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-300">
                  お名前 <span className="text-red-400">*</span>
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
                  placeholder="山田 太郎"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300">
                メールアドレス <span className="text-red-400">*</span>
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
                placeholder="example@company.co.jp"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-slate-300">
                電話番号
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
                placeholder="03-1234-5678"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="article_url" className="text-slate-300">
                診断希望の記事URL
              </Label>
              <Input
                id="article_url"
                name="article_url"
                type="url"
                value={formData.article_url}
                onChange={handleChange}
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
                placeholder="https://example.com/article"
              />
              <p className="text-slate-500 text-xs">
                ※ 後からでも共有いただけます
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message" className="text-slate-300">
                ご要望・ご質問
              </Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 resize-none"
                placeholder="診断の目的やご要望があればご記入ください"
              />
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="application_button w-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold py-6 transition-all hover:shadow-lg hover:shadow-cyan-500/25"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  送信中...
                </>
              ) : (
                '無料診断を申し込む'
              )}
            </Button>

            <p className="text-slate-500 text-xs text-center">
              お預かりした情報は、本サービスのご案内のみに利用いたします。
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ContactModal;